import { Hub } from '@aws-amplify/core';
import {
  resolveInactivityTimeoutMs,
  resolveInactivityWarningMs,
} from '@chiroup/core/constants/inactivityLock';
import { MILLISECONDS } from '@chiroup/core/constants/time';
import { getExtendedSettingValueByKey, Me } from '@chiroup/core/types/Me.type';
import { useInactivityTimer } from '@chiroup/hooks/useInactivityTimer';
import React, { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import InactivityLockOverlay from '../components/common/inactivity/InactivityLockOverlay';
import userService from '../services/user.service';
import { enhancedSignOut } from '../utils/authUtils';
import { MeContext } from './me.context';
import { ToastContext, ToastTypes } from './toast.context';

type LockContextValue = {
  isLocked: boolean;
  lockNow: () => void;
};

export const InactivityLockContext = React.createContext<LockContextValue>({
  isLocked: false,
  lockNow: () => {},
});

const STORAGE_KEY_ACTIVITY = 'chiroup.lastActivity';
const STORAGE_KEY_LOCK = 'chiroup.locked';
const TOAST_NAME = 'inactivity-warning';

const InactivityCountdown: React.FC<{ lockAt: number }> = ({ lockAt }) => {
  const compute = () => Math.max(0, Math.ceil((lockAt - Date.now()) / 1000));
  const [secondsLeft, setSecondsLeft] = useState(compute);
  const lockAtRef = useRef(lockAt);
  lockAtRef.current = lockAt;

  useEffect(() => {
    setSecondsLeft(compute);
    const id = window.setInterval(() => {
      const next = Math.max(0, Math.ceil((lockAtRef.current - Date.now()) / 1000));
      setSecondsLeft(next);
      if (next === 0) window.clearInterval(id);
    }, 1000);

    return () => window.clearInterval(id);
    // compute() only depends on lockAtRef, kept fresh above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockAt]);

  const label = secondsLeft === 1 ? 'second' : 'seconds';

  return (
    <>
      For security, your session will lock in{' '}
      <span className="font-semibold tabular-nums">{secondsLeft}</span> {label} due to inactivity.
    </>
  );
};

type Props = {
  children?: React.ReactNode;
};

export const InactivityLockProvider: React.FC<Props> = ({ children }) => {
  const { me } = use(MeContext);
  const { createToast, removeToastByName } = use(ToastContext);
  const queryClient = useQueryClient();
  const [isLocked, setIsLocked] = useState(false);

  // Resolve the timeout from the selected clinic's ClinicExtendedSettings.
  // `null` here means the lock is disabled — either the clinic explicitly
  // opted out, or the clinic is non-EHR (Chiroup-only, no PHI floor) and has
  // never saved a preference. Anything outside the allowed list snaps to the
  // clinic's EHR-aware default — never to a longer value. Recomputes when the
  // selected clinic changes, so switching clinics mid-session immediately
  // uses the new clinic's policy.
  const { lockTimeoutMs, lockWarningMs, warningLeadMs, lockEnabled } = useMemo(() => {
    const configured = getExtendedSettingValueByKey(
      me?.selectedClinic ?? null,
      'inactivityLockTimeoutMinutes'
    );
    const timeoutMs = resolveInactivityTimeoutMs(configured, me?.selectedClinic?.ehr);

    if (timeoutMs === null) {
      return { lockTimeoutMs: 0, lockWarningMs: 0, warningLeadMs: 0, lockEnabled: false };
    }

    const leadMs = resolveInactivityWarningMs(timeoutMs);

    return {
      lockTimeoutMs: timeoutMs,
      lockWarningMs: Math.max(0, timeoutMs - leadMs),
      warningLeadMs: leadMs,
      lockEnabled: true,
    };
  }, [me?.selectedClinic]);

  const enabled = !!me?.ID && !isLocked && lockEnabled;

  // Server is the source of truth: when /me reports locked, reflect that into
  // the overlay. Covers refreshes, fresh sign-ins on a previously-locked
  // account, and cross-device lock state.
  useEffect(() => {
    if (me?.locked) {
      setIsLocked(true);
    }
  }, [me?.locked]);

  // Any 423 sessionLocked response from the API (dispatched by the query/mutation
  // cache in App.tsx) means another tab or device locked the user — sync this
  // tab without waiting for the next /me refetch.
  useEffect(() => {
    if (!me?.ID) return;

    const onSessionLocked = () => setIsLocked(true);

    window.addEventListener('chiroup:session-locked', onSessionLocked);

    return () => window.removeEventListener('chiroup:session-locked', onSessionLocked);
  }, [me?.ID]);

  // Session-expired sign-out is handled at the App level (always mounted),
  // not here. That matters because /me itself can return 401 sessionExpired
  // on a cold boot, before Container has rendered MeContext — which means
  // this provider hasn't mounted yet and a listener here would never fire.
  // See App.tsx for the listener.

  const handleWarning = useCallback(() => {
    removeToastByName(TOAST_NAME);
    const lockAt = Date.now() + warningLeadMs;
    createToast({
      title: 'Session locking soon',
      description: <InactivityCountdown lockAt={lockAt} />,
      type: ToastTypes.Info,
      duration: warningLeadMs,
      name: TOAST_NAME,
    });
  }, [createToast, removeToastByName, warningLeadMs]);

  const handleLock = useCallback(() => {
    removeToastByName(TOAST_NAME);

    try {
      localStorage.setItem(STORAGE_KEY_LOCK, String(Date.now()));
    } catch {
      // localStorage may be unavailable — degrade silently.
    }

    // Best-effort audit write; never block the lock UI on it.
    userService.sessionLockEvent().catch(() => {
      // Network/auth error — Sentry breadcrumb is fine here, but we
      // intentionally swallow so a degraded backend can't prevent locking.
    });
    setIsLocked(true);
  }, []);

  const handleActivity = useCallback(() => {
    removeToastByName(TOAST_NAME);
  }, [removeToastByName]);

  useInactivityTimer({
    timeoutMs: lockTimeoutMs,
    warningMs: lockWarningMs,
    onWarning: handleWarning,
    onLock: handleLock,
    onActivity: handleActivity,
    enabled,
    storageKey: STORAGE_KEY_ACTIVITY,
  });

  // Cross-tab lock state: if one tab locks (sets the key), all tabs lock; if
  // one tab unlocks (removes the key), all tabs unlock. The `storage` event
  // doesn't fire in the tab that performed the change, so this listener
  // handles the OTHER tabs only.
  useEffect(() => {
    if (!me?.ID) return;

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY_LOCK) return;

      // removeItem from another tab → unlock signal. (Server has already
      // deleted the per-session LockedSession row, so subsequent API calls
      // from this tab will succeed.)
      if (e.newValue === null) {
        setIsLocked(false);

        return;
      }

      // Stale value (e.g., from a refreshed tab) — only lock if recent.
      const ts = Number(e.newValue);

      if (!Number.isFinite(ts)) return;
      if (Date.now() - ts > MILLISECONDS.inMinutes(1)) return;
      setIsLocked(true);
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, [me?.ID]);

  // Sign-out clears the local lock flag so the login page isn't covered.
  // (The `signedIn` handler — including the sessionResume() GC — lives in
  // App.tsx because this provider mounts AFTER /me resolves, by which time
  // the signedIn event has already fired and Amplify Hub doesn't replay it.)
  useEffect(() => {
    const stop = Hub.listen('auth', data => {
      if (data.payload.event === 'signedOut') {
        try {
          localStorage.removeItem(STORAGE_KEY_LOCK);
        } catch {
          // ignore
        }
        setIsLocked(false);
      }
    });

    return () => stop?.();
  }, []);

  const unlock = useCallback(async () => {
    try {
      localStorage.removeItem(STORAGE_KEY_LOCK);
      // Bump activity so the timer in this tab starts fresh.
      localStorage.setItem(STORAGE_KEY_ACTIVITY, String(Date.now()));
    } catch {
      // ignore
    }
    // Only refetch /me when the cached payload is the minimal locked one
    // (i.e. the user refreshed while locked). For an in-session lock the
    // cached `me` still has full clinics + selectedClinic and matches the
    // post-unlock server state already — refetching here would replace the
    // me object reference, fire useMe's useEffect, and the brief window
    // where the new me lacks `selectedClinic` makes Container render
    // NoClinicSelected for one frame. That's the "refresh" flash.

    const cached = queryClient.getQueryData<Me | undefined>(['me']);

    if (cached?.locked) {
      await queryClient.invalidateQueries(['me']);
    }

    setIsLocked(false);
  }, [queryClient]);

  const sessionExpired = useCallback(async () => {
    try {
      localStorage.removeItem(STORAGE_KEY_LOCK);
    } catch {
      // ignore
    }

    setIsLocked(false);
    // enhancedSignOut clears tokens + redirects to login via the Hub
    // 'signedOut' event handled in App.tsx.
    await enhancedSignOut();
  }, []);

  const lockNow = useCallback(() => {
    handleLock();
  }, [handleLock]);

  const value = useMemo<LockContextValue>(() => ({ isLocked, lockNow }), [isLocked, lockNow]);

  // When the server reports locked, /me returned only the unlock-overlay
  // payload — clinics aren't present and rendering the app tree would push
  // the user toward /create-clinic and trigger 423s from background queries.
  // For in-session locks (isLocked=true but server hasn't been re-read yet),
  // cached `me` still has clinics so children stay mounted and React Query
  // state is preserved across the lock.
  const showChildren = !me?.locked;

  return (
    <InactivityLockContext.Provider value={value}>
      {showChildren ? children : null}
      <InactivityLockOverlay
        open={(isLocked || !!me?.locked) && !!me?.ID}
        hasPinConfigured={!!me?.hasPinConfigured}
        userEmail={me?.email ?? ''}
        onUnlock={unlock}
        onSessionExpired={sessionExpired}
      />
    </InactivityLockContext.Provider>
  );
};
